import { CustomerEntity } from 'Models/Entities';
import { riskScoreOptions } from 'Models/Enums';
import { store } from 'Models/Store';
import { Dictionary } from 'Util/CustomTypes';
import { formatAbn } from 'Util/StringUtils';
import riskScoreBox from 'Views/Components/Intel/RiskScoreBox';
import classNames from 'classnames';
import moment from 'moment';
import { ReactNode } from 'react';
import { CountWithIcon } from '../CountWithIcon';
import { MonitoredSection, withMonitoring } from './MonitoredSection';

export interface CustomerColumnType {
	defaultDisplayName: string,
	gqlSnippet: string,
	displayFunction: (customer: CustomerEntity) => ReactNode | string,
	stringDisplayFunction?: (customer: CustomerEntity) => string,
	sortColumn?: string,
	tooltip?: string,
	onClickValue?: (customer: CustomerEntity) => void,
}

function getNewestATBGraphqlSnippet(field : string) {
	return `atbRecords (orderBy: [{path: "atbFile.targetMonth", descending:true}, {path: "atbFile.modified",
				descending: true}], take: 1, where: [{path: "atbFile.atbProcessingStatus", value: ["PROCESSED"]}]) {
			id
			${field}
		}
	`;
}

function getMonitoredGqlSnippet(field: string) {
	return `
		monitoredss {
			monitoreds {
				id
				${field}
			}
		}
	`;
}

function getAtoDebtGqlSnippet(field: string){
	const atoDebt = `
		atoDebt {
			id
			modified
			${field}
		}
	`;
	return getMonitoredGqlSnippet(atoDebt);
}

const CustomerColumnTypes = {
	atoDebt: {
		defaultDisplayName: 'ATO Debt',
		gqlSnippet: getAtoDebtGqlSnippet('amount'),
		displayFunction: (customer: CustomerEntity) => {
			let atoDebtAmount = '$0';
			const monitoredCustomers = customer.monitoredss.map(m => m.monitoreds).filter(m => !!m);
			const atoDebts = monitoredCustomers.map(m => m.atoDebt).filter(m => !!m);
			const atoDebtsWithinWeeks = atoDebts?.filter(atoDebt => atoDebt?.modified && moment.utc(atoDebt.modified).isSameOrAfter(moment().utc().subtract(1, 'weeks')));
			const amount = atoDebtsWithinWeeks?.map(m => m?.amount ?? 0).reduce((a, b) => a + b, 0);
			atoDebtAmount = `$${amount}`;
			return (
				<MonitoredSection status={customer.monitoredStatus} displayType='icon' showTooltip>
					<p>{atoDebtAmount}</p>
				</MonitoredSection>
			);
		},
		tooltip: 'This is the total amount of debt owed to the ATO',
		stringDisplayFunction: (customer: CustomerEntity) => {
			const callback = () => {
				const monitoredCustomers = customer.monitoredss.map(m => m.monitoreds).filter(m => !!m);
				if (monitoredCustomers.length === 0) {
					return '$0';
				}
				const atoDebts = monitoredCustomers.map(m => m.atoDebt).filter(m => !!m);
				if (atoDebts.length === 0) {
					return '$0';
				}
				const amount = atoDebts?.map(m => m?.amount ?? 0).reduce((a, b) => a + b, 0);
				return `$${amount}`;
			}

			return withMonitoring(customer.monitoredStatus, callback);
		}
	},
	debtorID: {
		defaultDisplayName: 'Debtor ID',
		gqlSnippet: 'debtorID',
		displayFunction: (customer: CustomerEntity) => customer.debtorID,
		sortColumn: 'debtorID',
	},
	businessName: {
		defaultDisplayName: 'Business Name',
		gqlSnippet: 'businessName',
		displayFunction: (customer: CustomerEntity) => customer.businessName,
		sortColumn: 'businessName',
	},
	abn: {
		defaultDisplayName: 'ABN',
		gqlSnippet: 'abn',
		displayFunction: (customer: CustomerEntity) => formatAbn(customer.abn),
		sortColumn: 'abn',
	},
	entityStatus: {
		defaultDisplayName: 'Entity status',
		gqlSnippet: 'businessEntityStatus',
		displayFunction: (customer: CustomerEntity) => (
			<MonitoredSection status={customer.monitoredStatus} displayType='icon' showTooltip>
				<span
					className={classNames('entity-status-icon', {
						valid: customer.businessEntityStatus === true,
						invalid: customer.businessEntityStatus === false,
						fetching: customer.businessEntityStatus === null,
					})}
				/>
			</MonitoredSection>
		),
		stringDisplayFunction: (customer: CustomerEntity) => {
			const callback = () => {
				if (customer.businessEntityStatus === null) {
					return 'fetching';
				}
				return customer.businessEntityStatus ? 'valid' : 'invalid';
			};
			return withMonitoring(customer.monitoredStatus, callback);
		},
		sortColumn: 'businessEntityStatus',
	},
	businessEntities: {
		defaultDisplayName: 'Business Entity',
		gqlSnippet:
			`businessEntityId
			businessEntity {
				id
				name
			}`,
		displayFunction: (customer: CustomerEntity) => customer.businessEntity?.name ?? 'N/A',
		tooltip: 'Supplying entity',
	},
	groups: {
		defaultDisplayName: 'Groups',
		gqlSnippet:
			`groupss {
				id
				groups {
					id
					name
				}
			}`,
		displayFunction: (customer: CustomerEntity) => {
			const groupNames = customer.groupss.map(g => g.groups?.name).filter(name => name);
			return groupNames.length > 0 ? groupNames.join(', ') : 'N/A';
		},
		tooltip: 'Attached groups',
	},
	insolvencyRiskRating: {
		defaultDisplayName: 'Insolvency Risk Rating',
		gqlSnippet: `
			newestAlares {
				id
				insolvencyRiskRating
			}
		`,
		displayFunction: (customer: CustomerEntity) => (
			<MonitoredSection status={customer.monitoredStatus} displayType='icon' showTooltip>
				{riskScoreBox({
					score: customer.newestAlares?.insolvencyRiskRating,
				})}
			</MonitoredSection>
		),
		stringDisplayFunction: (customer: CustomerEntity) => {
			const callback = () => riskScoreOptions[customer.newestAlares?.insolvencyRiskRating || 'UNKNOWN'];
			return withMonitoring(customer.monitoredStatus, callback);
		},
		sortColumn: 'insolvencyRiskRating',
		tooltip: 'This is the probability of the customer becoming insolvent in the coming 12 months',
	},
	adverseAlerts: {
		defaultDisplayName: 'Adverse Alerts',
		gqlSnippet: `
			newestAlares {
				id
				adverseAlerts
			}
		`,
		displayFunction: (customer: CustomerEntity) => (
			<MonitoredSection status={customer.monitoredStatus} displayType='icon' showTooltip>
				{CountWithIcon({
					count: customer?.newestAlares?.adverseAlerts,
					icon: 'warning-2',
				})}
			</MonitoredSection>
		),
		stringDisplayFunction: (customer: CustomerEntity) => {
			const callback = () => customer?.newestAlares?.adverseAlerts.toString();
			return withMonitoring(customer.monitoredStatus, callback);
		},
		sortColumn: 'adverseAlerts',
		tooltip: 'This links to details of events or information which indicates risk such as court action',
		onClickValue: customer => {
			if (customer.monitoredStatus !== 'MONITORED') {
				return;
			}
			store.routerHistory
				.push(`/monitor/customers/${customer.id}`,
					{ navigateTo: 'adverse-alerts' });
		},
	},
	paymentRiskRating: {
		defaultDisplayName: 'Payment Risk Rating',
		gqlSnippet: 'paymentRiskRating',
		displayFunction: (customer: CustomerEntity) => (
			<MonitoredSection status={customer.monitoredStatus} displayType='icon' showTooltip>
				{riskScoreBox({
					score: customer.paymentRiskRating ?? 'UNKNOWN',
				})}
			</MonitoredSection>
		),
		stringDisplayFunction: (customer: CustomerEntity) => {
			const callback = () => (riskScoreOptions[customer.paymentRiskRating ?? 'UNKNOWN']);
			return withMonitoring(customer.monitoredStatus, callback);
		},
		sortColumn: 'customPaymentRiskRating',
		tooltip: 'Payment Risk Rating updated on 15th of each month based on the most recent ATB data. If you don’t upload your ATB the risk indicator may not be current or be displayed.',
	},
	totalDebt: {
		defaultDisplayName: 'Total Outstanding',
		gqlSnippet: getNewestATBGraphqlSnippet('totalDebt'),
		displayFunction: (customer: CustomerEntity) => customer.atbRecords.length > 0
			? `$${customer.atbRecords[0].totalDebt ?? 0}`
			: '',
		sortColumn: 'atbFile totalOutstanding',
		tooltip: 'This is the total amount owed',
	},
	totalOverdue: {
		defaultDisplayName: 'Total Overdue',
		gqlSnippet: getNewestATBGraphqlSnippet('totalOverdue'),
		displayFunction: (customer: CustomerEntity) => customer.atbRecords.length > 0
			? `$${customer.atbRecords[0].totalOverdue ?? 0}`
			: '',
		sortColumn: 'atbFile totalOverdue',
		tooltip: 'This is the total amount owing that is beyond agreed payment terms',
	},
	currentDebt: {
		defaultDisplayName: 'Current Debt',
		gqlSnippet: getNewestATBGraphqlSnippet('dpcurrent'),
		displayFunction: (customer: CustomerEntity) => customer.atbRecords.length > 0
			? `$${customer.atbRecords[0].dpcurrent ?? 0}`
			: '',
		sortColumn: 'atbFile currentDebt',
		tooltip: 'This is the amount owing that is not past its due date',
	},
	dp1to30: {
		defaultDisplayName: '1-30 DPD',
		gqlSnippet: getNewestATBGraphqlSnippet('dp1to30'),
		displayFunction: (customer: CustomerEntity) => customer.atbRecords.length > 0
			? `$${customer.atbRecords[0].dp1to30 ?? 0}`
			: '',
		sortColumn: 'atbFile dp1to30',
		tooltip: 'This is the amount that is between 1 day and 30 days overdue',
	},
	dp31to60: {
		defaultDisplayName: '31-60 DPD',
		gqlSnippet: getNewestATBGraphqlSnippet('dp31to60'),
		displayFunction: (customer: CustomerEntity) => customer.atbRecords.length > 0
			? `$${customer.atbRecords[0].dp31to60 ?? 0}`
			: '',
		sortColumn: 'atbFile dp31to60',
		tooltip: 'This is the amount that is between 31 days and 60 days overdue',
	},
	dp61to90: {
		defaultDisplayName: '61-90 DPD',
		gqlSnippet: getNewestATBGraphqlSnippet('dp61to90'),
		displayFunction: (customer: CustomerEntity) => customer.atbRecords.length > 0
			? `$${customer.atbRecords[0].dp61to90 ?? 0}`
			: '',
		sortColumn: 'atbFile dp61to90',
		tooltip: 'This is the amount that is between 61 days and 90 days overdue',
	},
	dp91to120: {
		defaultDisplayName: '91-120 DPD',
		gqlSnippet: getNewestATBGraphqlSnippet('dp91to120'),
		displayFunction: (customer: CustomerEntity) => customer.atbRecords.length > 0
			? `$${customer.atbRecords[0].dp91to120 ?? 0}`
			: '',
		sortColumn: 'atbFile dp91to120',
		tooltip: 'This is the amount that is between 91 days and 120 days overdue',
	},
	dp120plus: {
		defaultDisplayName: '120+ DPD',
		gqlSnippet: getNewestATBGraphqlSnippet('dp120plus'),
		displayFunction: (customer: CustomerEntity) => customer.atbRecords.length > 0
			? `$${customer.atbRecords[0].dp120plus ?? 0}`
			: '',
		sortColumn: 'atbFile dp120plus',
		tooltip: 'This is the amount that is more than 120 days overdue',
	},
} as Dictionary<CustomerColumnType>;
export default CustomerColumnTypes;
